import React from 'react';
import usePasswordResetFlow from '@tcs/client-portal-common/hooks/usePasswordResetFlow';
import {
  Banner,
  Box,
  Button,
  Fade,
  Typography,
  useNavigate,
} from '@forgeglobal/design-framework';
import SimpleStepper, {
  stepType,
} from '@tcs/common/components/atoms/SimpleStepper';
import IdentificationForm from './IdentificationForm';
import SecurityQuestionsForm from './SecurityQuestionsForm';
import DialogPageLayout from '@tcs/client-portal-common/components/organisms/DialogLayout/DialogPageLayout';
import ResetComplete from './ResetComplete';
import DialogBody from '@tcs/client-portal-common/components/organisms/DialogLayout/DialogBody';
import NeedHelp from '@tcs/common/components/atoms/footer/NeedHelp';
import SecurityQuestionIsEmptyForm from './SecurityQuestionIsEmptyForm';
import { useStyles } from './style';

export enum ResetPasswordSteps {
  Identification = 0,
  SecurityQuestions = 1,
  PasswordResetComplete = 2,
  SecurityQuestionIsEmpty = 3,
}

export const errors = {
  GETQUESTION:
    'The account number and/or email address you entered do not match our records. Please try again',
  RESETPASSWORD: 'Incorrect answer, please try again',
};

const ResetPassword = () => {
  const classes = useStyles();
  const nav = useNavigate();
  const {
    activeStep,
    question,
    error,
    setIdentity,
    setActiveStep,
    resetPassword,
    clearError,
  } = usePasswordResetFlow();

  const handleOnCancel = () => nav('/login', { replace: true });

  if (activeStep === ResetPasswordSteps.PasswordResetComplete) {
    return (
      <DialogPageLayout showCloseArrow={false}>
        <DialogBody type={'message'}>
          <ResetComplete close={handleOnCancel} />
        </DialogBody>
      </DialogPageLayout>
    );
  }

  const renderActiveStep = () => {
    switch (activeStep) {
      case ResetPasswordSteps.Identification:
        return (
          <IdentificationForm
            onCancel={handleOnCancel}
            onSubmit={setIdentity}
          />
        );
      case ResetPasswordSteps.SecurityQuestions:
        return (
          <SecurityQuestionsForm
            onCancel={() => setActiveStep(ResetPasswordSteps.Identification)}
            onSubmit={resetPassword}
            question={question}
          />
        );
      case ResetPasswordSteps.SecurityQuestionIsEmpty:
        return (
          <SecurityQuestionIsEmptyForm
            onCancel={() => setActiveStep(ResetPasswordSteps.Identification)}
          />
        );
    }
  };

  return (
    <DialogPageLayout
      handleExit={handleOnCancel}
      header={
        error && (
          <Fade in={true} timeout={750}>
            <div>
              <Banner
                color="error"
                className={classes.error}
                action={
                  <Button
                    size="small"
                    /*color="default"  TODO - review the correct color*/
                    variant="text"
                    onClick={clearError}
                    data-test-id="password-success-button">
                    DISMISS
                  </Button>
                }>
                <Typography
                  variant="body2"
                  color="textPrimary"
                  data-test-id="reset-password-error-message">
                  {error}
                </Typography>
              </Banner>
            </div>
          </Fade>
        )
      }>
      <DialogBody type={'form'}>
        <Box className={classes.dialogBox}>
          <Typography variant="h5" data-test-id="sub-title">
            Reset Password
          </Typography>
        </Box>
        <Box className={classes.dialogBox}>
          <SimpleStepper
            activeStep={activeStep}
            steps={[
              {
                title: 'Identification',
                type: stepType.success,
              },
              {
                title: 'Security question',
                type:
                  activeStep === ResetPasswordSteps.SecurityQuestionIsEmpty
                    ? stepType.error
                    : stepType.success,
              },
            ]}
          />
        </Box>
        {renderActiveStep()}
      </DialogBody>
      <div className={classes.needHelpWrapper}>
        <div className={classes.needHelp}>
          <NeedHelp />
        </div>
      </div>
    </DialogPageLayout>
  );
};

export default ResetPassword;
